import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, defer, map, switchMap, throwError } from 'rxjs';
import { InvisibleRecaptchaService } from 'src/app/core/service/invisible-recaptcha.service';
import { environment } from 'src/environments/environment';
import { SendVerificationCodeRequest, ValidateVerificationCodeRequest } from '../model/verification-code.model';

@Injectable({
  providedIn: 'root'
})
export class ValidationCodeService {
  private readonly captchaAction = 'contact_verification';

  constructor(
    private readonly http: HttpClient,
    private readonly captchaSrv: InvisibleRecaptchaService,
  ) {}

  sendCode(request: Omit<SendVerificationCodeRequest, 'captcha'>): Observable<string> {
    return this.getCaptcha().pipe(
      switchMap(captcha => {
        const body: SendVerificationCodeRequest = { ...request, captcha };
        return this.http.post<unknown>(`${environment.webApiUrl}/api/send-verification-code`, body);
      }),
      map(response => {
        if (typeof response !== 'string') {
          throw new Error('No pudimos confirmar el envío del código. Intentá nuevamente.');
        }
        return response;
      }),
      catchError((error: unknown) => this.handleError(error)),
    );
  }

  validateCode(request: Omit<ValidateVerificationCodeRequest, 'captcha'>): Observable<boolean> {
    return this.getCaptcha().pipe(
      switchMap(captcha => {
        const body: ValidateVerificationCodeRequest = { ...request, captcha };
        return this.http.post<unknown>(`${environment.webApiUrl}/api/validate-verification-code`, body);
      }),
      map(response => {
        if (response !== true && response !== false) {
          throw new Error('No pudimos confirmar la verificación. Intentá nuevamente.');
        }
        return response;
      }),
      catchError((error: unknown) => this.handleError(error)),
    );
  }

  private getCaptcha(): Observable<string> {
    return defer(() => this.captchaSrv.getCaptcha(this.captchaAction)).pipe(
      catchError(() => throwError(() => new Error('No pudimos realizar la verificación de seguridad. Intentá nuevamente.'))),
      map(token => {
        if (!token.trim()) {
          throw new Error('No pudimos realizar la verificación de seguridad. Intentá nuevamente.');
        }
        return token;
      }),
    );
  }

  private handleError(error: unknown): Observable<never> {
    if (!(error instanceof HttpErrorResponse)) {
      return throwError(() => error);
    }
    const body: unknown = error.error;
    const message = typeof body === 'string' ? body : '';
    if (error.status === 500 && /captcha inv[aá]lido/i.test(message)) {
      return throwError(() => new Error('No pudimos validar la verificación de seguridad. Intentá nuevamente.'));
    }
    if (error.status === 400) {
      return throwError(() => new Error('Revisá los datos ingresados e intentá nuevamente.'));
    }
    return throwError(() => new Error('No pudimos comunicarnos con el servicio de verificación. Intentá nuevamente.'));
  }
}
